/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import { ROUTES } from '../../../constants/routes';
import { useAppSelector } from '../../../reducers';
import { generateUrl } from '../../../routes/util';
import { getCurrentUser } from '../../../selectors/user';
import { OnboardingMatches } from '../types';
import { getDefaultConfig } from '../../../config/app-config';
import { AppConfig } from '../../../types/app';

const getNameString = (items: { name: string }[]): string => {
  if (items.length === 0) {
    return '';
  }

  if (items.length === 1) {
    return items[0].name;
  }

  const lastItem = items[items.length - 1];
  return [
    items.slice(0, items.length - 1).map(i => i.name).join(', '),
    lastItem?.name
  ].join(' and ');
}

export const RequestSent = ({ appConfig, requests }: { appConfig: AppConfig, requests: OnboardingMatches[] }) => {

  const history = useHistory();
  const currentUser = useAppSelector(getCurrentUser);

  const {
    openRequests,
    acceptedRequests
  } = useMemo(() => {
    if (!currentUser) {
      return {
        openRequests: requests,
        acceptedRequests: []
      }
    }
    const userInitiativeIds = currentUser.permissions.map(p => p.initiativeId);
    return {
      openRequests: requests.filter(r => !userInitiativeIds.includes(r._id)),
      acceptedRequests: requests.filter(r => userInitiativeIds.includes(r._id))
    }
  }, [currentUser, requests]);

  const handleGoToCompany = (initiativeId: string) => {
    const returnPath = generateUrl(ROUTES.COMPANY_TRACKER_LIST, {
      rootAppPath: appConfig.rootAppPath ?? getDefaultConfig().branding.ctlApp.rootAppPath,
      initiativeId
    });
    history.push(returnPath);
  }

  return (
    <div className='mx-5'>
      {openRequests.length > 0 ? (
        <>
          <div className='h4 text-strong mb-3'>Request Sent</div>
          <div className='mt-4'>
            <p>A request to join <strong>{getNameString(openRequests)}</strong> has now been sent to the Owner the company(s).</p>
            <p>You will receive an email when access has been granted,
              and then you can login using your email address and the password that you have set up.
            </p>
          </div>
        </>
      ) : null}
      {acceptedRequests.length > 0 ? (
        <>
          <div className='h4 text-strong mb-3'>Requests Accepted</div>
          {acceptedRequests.map(request => (
            <div className='mt-4'>
              <div>
                {request.name}
                <Button color='primary' onClick={() => handleGoToCompany(request._id)}>Access now</Button>
              </div>
            </div>
          ))}
        </>
      ) : null}
    </div >
  )
}
